import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Avatar,
  Typography,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useGetList } from 'react-admin';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface UserAutocompleteProps {
  value?: string | string[] | null;
  onChange: (userId: string | string[] | null) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  multiple?: boolean;
}

const getUserInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (name: string): string => {
  const colors = [
    '#1976d2', '#dc004e', '#9c27b0', '#673ab7',
    '#3f51b5', '#2196f3', '#00bcd4', '#009688',
    '#4caf50', '#8bc34a', '#ff9800', '#ff5722',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  value,
  onChange,
  label = 'Assign To',
  required = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  disabled = false,
  multiple = false,
}) => {
  // Fetch users sorted alphabetically by name
  const { data: users = [], isLoading } = useGetList<User>('users', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  // Find the selected user(s)
  const selectedValue = multiple
    ? users.filter(u => Array.isArray(value) && value.includes(u.id))
    : users.find(u => u.id === value) || null;

  const handleChange = (_event: any, newValue: User | User[] | null) => {
    if (multiple) {
      if (Array.isArray(newValue)) {
        onChange(newValue.map(u => u.id));
      } else {
        onChange([]);
      }
    } else {
      onChange((newValue as User)?.id || null);
    }
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedValue}
      onChange={handleChange}
      options={users}
      loading={isLoading}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      getOptionLabel={(option) => option.name || option.email}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <ListItemAvatar>
            <Avatar
              src={option.avatar}
              sx={{
                width: 32,
                height: 32,
                bgcolor: !option.avatar ? getAvatarColor(option.name || option.email) : undefined,
                fontSize: '0.875rem',
              }}
            >
              {!option.avatar && (option.name ? getUserInitials(option.name) : <PersonIcon />)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={option.name || option.email}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </Box>
      )}
      renderInput={(params) => {
        const singleUser = (!multiple && selectedValue && !Array.isArray(selectedValue)) ? (selectedValue as User) : null;

        return (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {singleUser && (
                    <Avatar
                      src={singleUser.avatar}
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        bgcolor: !singleUser.avatar
                          ? getAvatarColor(singleUser.name || singleUser.email)
                          : undefined,
                        fontSize: '0.75rem',
                      }}
                    >
                      {!singleUser.avatar && (singleUser.name
                        ? getUserInitials(singleUser.name)
                        : <PersonIcon sx={{ fontSize: 14 }} />)}
                    </Avatar>
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
};